import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  BarChart3, TrendingUp, TrendingDown, Minus, RefreshCw, Camera, 
  AlertTriangle, CheckCircle2, XCircle, Info, Calendar, 
  DollarSign, Users, Target, Activity, AlertCircle, ClipboardPlus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSLiveScorecard = () => {
  const [scorecardData, setScorecardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [snapshotNotes, setSnapshotNotes] = useState('');
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [createIssueDialogOpen, setCreateIssueDialogOpen] = useState(false);
  const [selectedMetricForIssue, setSelectedMetricForIssue] = useState(null);

  const fetchScorecard = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      const response = await axios.get(`${API}/eos/live-scorecard`);
      setScorecardData(response.data);
      if (showToast) {
        toast.success('Scorecard refreshed');
      }
    } catch (error) {
      console.error('Error fetching scorecard:', error);
      toast.error('Failed to load scorecard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchScorecard();
  }, [fetchScorecard]);

  const handleCreateSnapshot = async () => {
    // This would normally require a meeting ID - for now show info
    toast.info('Snapshot feature requires an active L10 meeting. Open from the L10 Meeting Room.');
    setSnapshotDialogOpen(false);
  };

  const handleCreateIssue = async () => {
    if (!selectedMetricForIssue) return;
    
    try {
      const issueData = {
        title: `${selectedMetricForIssue.name} Off Track`,
        description: `The ${selectedMetricForIssue.name} metric is currently off track.\n\nWeekly: ${formatValue(selectedMetricForIssue.weekly?.actual, selectedMetricForIssue.measurement_type)} (Goal: ${formatValue(selectedMetricForIssue.weekly?.goal, selectedMetricForIssue.measurement_type)})\n\nData Source: ${selectedMetricForIssue.data_source}`,
        priority: selectedMetricForIssue.weekly?.status === 'red' ? 'high' : 'medium',
        category: selectedMetricForIssue.category,
        status: 'open'
      };
      
      await axios.post(`${API}/eos/issues`, issueData);
      toast.success('Issue created successfully');
      setCreateIssueDialogOpen(false);
      setSelectedMetricForIssue(null);
    } catch (error) {
      console.error('Error creating issue:', error);
      toast.error('Failed to create issue');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'green': return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'yellow': return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'red': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return <Minus className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'green': return 'bg-green-50 dark:bg-green-950/30';
      case 'yellow': return 'bg-yellow-50 dark:bg-yellow-950/30';
      case 'red': return 'bg-red-50 dark:bg-red-950/30';
      default: return 'bg-gray-50 dark:bg-gray-800/30';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-600" />;
      default: return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatValue = (value, type) => {
    if (value === null || value === undefined) return '—';
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-US', { 
          style: 'currency', 
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
      case 'percentage':
        return `${value}%`;
      case 'ratio':
        return value.toFixed(2);
      default:
        return typeof value === 'number' ? value.toLocaleString() : value;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'sales': return <Target className="h-4 w-4" />;
      case 'operations': return <Activity className="h-4 w-4" />;
      case 'financial': return <DollarSign className="h-4 w-4" />;
      default: return <BarChart3 className="h-4 w-4" />;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'sales': return 'Sales / Marketing';
      case 'operations': return 'Operations';
      case 'financial': return 'Financial';
      default: return category;
    }
  };

  const filterMetrics = (metrics) => {
    if (selectedCategory === 'all') return metrics;
    return metrics.filter(m => m.category === selectedCategory);
  };

  const formatPeriodLabel = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Calculating scorecard metrics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const metrics = scorecardData?.metrics || [];
  const summary = scorecardData?.summary || {};
  const period = scorecardData?.period || {};
  const diagnostics = scorecardData?.diagnostics || [];

  // Group metrics by category for display
  const salesMetrics = metrics.filter(m => m.category === 'sales');
  const opsMetrics = metrics.filter(m => m.category === 'operations');
  const finMetrics = metrics.filter(m => m.category === 'financial');

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="live-scorecard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <BarChart3 className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Live EOS Scorecard</h1>
              <p className="text-sm text-muted-foreground">
                Week of {formatPeriodLabel(period.week_start)} - {formatPeriodLabel(period.week_end)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setDiagnosticsOpen(true)}
              className="gap-2"
              data-testid="diagnostics-btn"
            >
              <AlertCircle className="h-4 w-4" />
              Diagnostics ({diagnostics.length})
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => fetchScorecard(true)}
              disabled={refreshing}
              className="gap-2"
              data-testid="refresh-scorecard-btn"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              size="sm"
              onClick={() => setSnapshotDialogOpen(true)}
              className="gap-2"
              data-testid="freeze-scorecard-btn"
            >
              <Camera className="h-4 w-4" />
              Freeze Scorecard
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold">{summary.total_tracked || 0}</div>
                <div className="text-sm text-muted-foreground">Total Metrics</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/50 dark:to-green-900/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{summary.green || 0}</div>
                <div className="text-sm text-green-700 dark:text-green-400">On Track</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-950/50 dark:to-yellow-900/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{summary.yellow || 0}</div>
                <div className="text-sm text-yellow-700 dark:text-yellow-400">Warning</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950/50 dark:to-red-900/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{summary.red || 0}</div>
                <div className="text-sm text-red-700 dark:text-red-400">Off Track</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{summary.health_score || 0}%</div>
                <div className="text-sm text-blue-700 dark:text-blue-400">Health Score</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Category Filter Tabs */}
        <Tabs defaultValue="all" value={selectedCategory} onValueChange={setSelectedCategory}>
          <TabsList className="grid w-full grid-cols-4 max-w-md">
            <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
            <TabsTrigger value="sales" data-testid="filter-sales">
              <Target className="h-4 w-4 mr-1" />
              Sales
            </TabsTrigger>
            <TabsTrigger value="operations" data-testid="filter-ops">
              <Activity className="h-4 w-4 mr-1" />
              Ops
            </TabsTrigger>
            <TabsTrigger value="financial" data-testid="filter-financial">
              <DollarSign className="h-4 w-4 mr-1" />
              Finance
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Scorecard Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Performance Metrics</CardTitle>
            <CardDescription>Click any off-track metric to create an Issue or To-Do</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="scorecard-table">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-semibold min-w-[200px]">Metric</th>
                    <th className="text-center p-3 font-semibold min-w-[120px]">Weekly</th>
                    <th className="text-center p-3 font-semibold min-w-[120px]">MTD</th>
                    <th className="text-center p-3 font-semibold min-w-[120px]">QTD</th>
                    <th className="text-center p-3 font-semibold min-w-[120px]">YTD</th>
                    <th className="text-center p-3 font-semibold w-[80px]">Trend</th>
                    <th className="text-center p-3 font-semibold w-[80px]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filterMetrics(metrics).map((metric, index) => {
                    const isOffTrack = metric.weekly?.status === 'red' || metric.weekly?.status === 'yellow';
                    return (
                      <tr 
                        key={metric.id} 
                        className={`border-b transition-colors hover:bg-muted/30 ${getStatusBg(metric.weekly?.status)}`}
                        data-testid={`metric-row-${metric.id}`}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(metric.weekly?.status)}
                            <div>
                              <div className="font-medium">{metric.name}</div>
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                {getCategoryIcon(metric.category)}
                                {getCategoryLabel(metric.category)}
                                {!metric.is_auto_calculated && (
                                  <Badge variant="outline" className="ml-1 text-[10px] py-0">External</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="space-y-0.5">
                            <div className="font-semibold">{formatValue(metric.weekly?.actual, metric.measurement_type)}</div>
                            {metric.weekly?.goal && (
                              <div className="text-xs text-muted-foreground">Goal: {formatValue(metric.weekly?.goal, metric.measurement_type)}</div>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="font-medium">{formatValue(metric.mtd?.actual, metric.measurement_type)}</div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="font-medium">{formatValue(metric.qtd?.actual, metric.measurement_type)}</div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="font-medium">{formatValue(metric.ytd?.actual, metric.measurement_type)}</div>
                        </td>
                        <td className="p-3 text-center">
                          {getTrendIcon(metric.weekly?.trend)}
                        </td>
                        <td className="p-3 text-center">
                          {isOffTrack && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedMetricForIssue(metric);
                                setCreateIssueDialogOpen(true);
                              }}
                              className="text-xs"
                              data-testid={`create-issue-${metric.id}`}
                            >
                              <ClipboardPlus className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Category Breakdown Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Sales Card */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-lg">Sales / Marketing</CardTitle>
              </div>
              <CardDescription>Lead flow & conversion metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {salesMetrics.slice(0, 5).map(m => (
                  <div key={m.id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{m.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{formatValue(m.weekly?.actual, m.measurement_type)}</span>
                      {getStatusIcon(m.weekly?.status)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Operations Card */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-orange-500" />
                <CardTitle className="text-lg">Operations</CardTitle>
              </div>
              <CardDescription>Production & quality metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {opsMetrics.slice(0, 5).map(m => (
                  <div key={m.id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{m.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{formatValue(m.weekly?.actual, m.measurement_type)}</span>
                      {getStatusIcon(m.weekly?.status)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Financial Card */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                <CardTitle className="text-lg">Financial</CardTitle>
              </div>
              <CardDescription>Revenue & profitability metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {finMetrics.slice(0, 5).map(m => (
                  <div key={m.id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{m.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{formatValue(m.weekly?.actual, m.measurement_type)}</span>
                      {getStatusIcon(m.weekly?.status)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Snapshot Dialog */}
        <Dialog open={snapshotDialogOpen} onOpenChange={setSnapshotDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Freeze Scorecard</DialogTitle>
              <DialogDescription>
                Create a snapshot of the current scorecard values. This is typically done during an L10 meeting.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Notes (Optional)</label>
                <Textarea
                  placeholder="Add any notes about this snapshot..."
                  value={snapshotNotes}
                  onChange={(e) => setSnapshotNotes(e.target.value)}
                />
              </div>
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="text-muted-foreground">
                  <Info className="h-4 w-4 inline mr-1" />
                  To create a snapshot, open the Scorecard tab from within an active L10 Meeting.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSnapshotDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateSnapshot}>Create Snapshot</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Diagnostics Dialog */}
        <Dialog open={diagnosticsOpen} onOpenChange={setDiagnosticsOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Scorecard Diagnostics</DialogTitle>
              <DialogDescription>
                Metrics that cannot be calculated due to missing data sources
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {diagnostics.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  All metrics are calculating successfully!
                </div>
              ) : (
                diagnostics.map((diag, idx) => (
                  <div key={idx} className="p-3 border rounded-lg bg-amber-50 dark:bg-amber-950/30">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div>
                        <div className="font-medium">{diag.metric_name}</div>
                        <div className="text-sm text-muted-foreground">{diag.reason}</div>
                        {diag.missing_data?.length > 0 && (
                          <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                            Missing: {diag.missing_data.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setDiagnosticsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Issue Dialog */}
        <Dialog open={createIssueDialogOpen} onOpenChange={setCreateIssueDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Issue from Metric</DialogTitle>
              <DialogDescription>
                Create an EOS Issue to address this off-track metric
              </DialogDescription>
            </DialogHeader>
            {selectedMetricForIssue && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusIcon(selectedMetricForIssue.weekly?.status)}
                    <span className="font-semibold">{selectedMetricForIssue.name}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Current:</span>
                      <span className="ml-2 font-medium">
                        {formatValue(selectedMetricForIssue.weekly?.actual, selectedMetricForIssue.measurement_type)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Goal:</span>
                      <span className="ml-2 font-medium">
                        {formatValue(selectedMetricForIssue.weekly?.goal, selectedMetricForIssue.measurement_type)}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  An issue will be created with the metric details. You can then discuss and resolve it during your L10 meeting.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateIssueDialogOpen(false);
                setSelectedMetricForIssue(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateIssue} data-testid="confirm-create-issue">
                Create Issue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EOSLiveScorecard;
